import {
  Button,
  IconName,
  List,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Tooltip,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Space } from '@fullstory/proto/fs/services/dx-objects';
import { useSelectableList } from '@fullstory/react-hooks';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CreateMenuSelect } from '../CreateMenuSelect';
import { noPermissionMessage } from '../../utils';

interface MenuListItemProps {
  iconName: IconName;
  title: string;
  rightIcon?: IconName;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  onClick: () => void;
  onHoveredChange?: (hovered: boolean) => void;
}

export const MenuListItem = React.forwardRef<HTMLLIElement, MenuListItemProps>(
  ({ iconName, title, rightIcon, disabled = false, tooltip, onClick, onHoveredChange }, ref) => {
    const option = useMemo(
      () => ({
        iconName,
        title,
        rightIcon,
      }),
      [iconName, title, rightIcon],
    );
    const options = useMemo(() => [option], [option]);
    const { getListItemProps } = useSelectableList(options, () => true, onClick, { active: true });
    const listItemProps = getListItemProps(option);
    const handleMouseEnter = () => {
      listItemProps.onMouseEnter();
      onHoveredChange?.(true);
    };
    const handleMouseLeave = () => {
      listItemProps.onMouseLeave();
      onHoveredChange?.(false);
    };

    return (
      <Tooltip disabled={!tooltip} placement="left" tooltipContent={tooltip}>
        <MenuItem
          ref={ref}
          {...listItemProps}
          disabled={disabled}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ListItemIcon size={16} name={iconName} color="default" />
          <ListItemText text={title} />
          {rightIcon && <ListItemIcon size={16} name={rightIcon} color="default" />}
        </MenuItem>
      </Tooltip>
    );
  },
);

MenuListItem.displayName = 'MenuListItem';

interface CreateButtonProps {
  orgId: string;
  space?: Space;
  enabledObjectTypes: React.ComponentProps<typeof CreateMenuSelect>['enabledObjectTypes'];
  onAddFromLibraryClick: () => void;
  addFromLibraryEnabled: boolean;
  newFSTAColors: boolean;
}

export const CreateButton: React.FC<CreateButtonProps> = ({
  orgId,
  space,
  enabledObjectTypes,
  onAddFromLibraryClick,
  addFromLibraryEnabled,
  newFSTAColors,
}) => {
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [createNewMenuOpen, setCreateNewMenuOpen] = useState(false);
  const createNewMenuHovered = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const createNewMenuOptionRef = useRef<HTMLLIElement>(null);

  const onCloseSignal = useCallback(() => setCreateNewMenuOpen(false), [setCreateNewMenuOpen]);

  return (
    <>
      <Button
        variant={newFSTAColors ? 'outline' : 'fill-secondary'}
        onClick={() => setAddMenuOpen(true)}
        iconRight="chevron-down"
        innerRef={buttonRef}
      >
        {intl.formatMessage({ defaultMessage: 'Create', id: 'VzzYJk' })}
      </Button>
      <Popover
        open={addMenuOpen}
        onCloseSignal={() => setAddMenuOpen(false)}
        anchor={buttonRef.current}
        placement="bottom-end"
        offset={8}
        portal={false}
        variant="bordered"
        width={309}
      >
        <List>
          <MenuListItem
            ref={createNewMenuOptionRef}
            iconName="plus"
            title={intl.formatMessage({ defaultMessage: 'Create a new...', id: 'rVPOh2' })}
            rightIcon="chevron-right"
            onClick={() => {
              setCreateNewMenuOpen(true);
            }}
            onHoveredChange={hovered => {
              if (hovered) {
                setCreateNewMenuOpen(hovered);
              } else {
                // give some time before attempting to close
                // this allows the user some time to navigate their mouse in the case
                // they dont take a perfect path keeping this list item hovered all the way over.
                setTimeout(() => {
                  if (!createNewMenuHovered.current) {
                    setCreateNewMenuOpen(hovered);
                  }
                }, 250);
              }
            }}
          />
          <MenuListItem
            iconName="plus-circle"
            disabled={!addFromLibraryEnabled}
            tooltip={!addFromLibraryEnabled && intl.formatMessage(noPermissionMessage)}
            title={intl.formatMessage({ defaultMessage: 'Add an existing...', id: 'OsnY9I' })}
            onClick={() => {
              setAddMenuOpen(false);
              onAddFromLibraryClick();
            }}
          />
        </List>
      </Popover>
      <CreateMenuSelect
        orgId={orgId}
        anchor={createNewMenuOptionRef.current}
        onCloseSignal={onCloseSignal}
        placement="left-start"
        offset={0}
        open={createNewMenuOpen}
        activeSpace={space}
        enabledObjectTypes={enabledObjectTypes}
        onMouseEnter={() => {
          createNewMenuHovered.current = true;
        }}
        onMouseLeave={() => {
          createNewMenuHovered.current = false;
          setCreateNewMenuOpen(false);
        }}
        location="Space library create button"
      />
    </>
  );
};
